import React, { useEffect, useState } from 'react';
import Breadcrumb from '../../../components/Breadcrumb';
import {
  createExternal,
  updateExternal,
  getExternalById,
} from '../../../axios/ExternalAxios';
import Swal from 'sweetalert2';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LoadingOverlay from '../../../components/LoadingOverlay';

function ExternalAdd() {
  const navigation = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    external_code: '',
    name: '',
    npwp: '',
    contact_name: '',
    contact_title: '',
    address: '',
    city: '',
    state_or_province: '',
    postal_code: '',
    country: '',
    email: '',
    phone: '',
    fax: '',
    payment_terms: '',
    notes: '',
    flag: '',
    segment_type: '',
  });

  const [breadcrumbItems, setBreadcrumbItems] = useState([
    { label: 'Dashboard', href: '/' },
    { label: 'External', href: '/external' },
    { label: 'Create External' },
  ]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Set breadcrumb based on the presence of 'id'
  useEffect(() => {
    if (id) {
      setBreadcrumbItems([
        { label: 'Dashboard', href: '/' },
        { label: 'External', href: '/external' },
        { label: 'Update External' },
      ]);
    }
  }, [id]);

  // Fetch external data if 'id' exists (for update)
  useEffect(() => {
    if (id) {
      const fetchExternalData = async () => {
        setMessage('Fetching data...');
        setLoading(true);
        try {
          const response = await getExternalById(id); // Get data by ID from backend
          setFormData(response); // Populate form with fetched data
        } catch (error) {
          console.error('Error fetching external data:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchExternalData();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      return;
    }

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary mx-1', // Bootstrap primary button
        denyButton: 'btn btn-danger mx-1', // Bootstrap warning button
        cancelButton: 'btn btn-secondary mx-1', // Bootstrap secondary button
      },
      buttonsStyling: false,
    });

    try {
      const result = await swalWithBootstrapButtons.fire({
        title: 'Do you want to save the data?',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: `Don't save`,
      });

      if (result.isConfirmed) {
        try {
          setMessage('Saving data...');
          setLoading(true);
          if (id) {
            await updateExternal(id, formData);
            swalWithBootstrapButtons.fire(
              'Saved!',
              'The external has been updated.',
              'success'
            );
          } else {
            await createExternal(formData);
            // Jika berhasil, tampilkan pesan sukses
            swalWithBootstrapButtons.fire(
              'Saved!',
              'The external has been created.',
              'success'
            );
          }
          navigation('/external');
        } catch (createAccountError) {
          // Jika ada kesalahan saat membuat akun, tampilkan pesan kesalahan
          swalWithBootstrapButtons.fire({
            title: 'Error!',
            text: createAccountError.message,
            icon: 'error',
          });
        } finally {
          setLoading(false);
        }
      } else if (result.isDenied) {
        swalWithBootstrapButtons.fire('Changes are not saved', '', 'info');
      }
    } catch (error) {
      swalWithBootstrapButtons.fire({
        title: 'Error!',
        text: 'There was an error processing your request.',
        icon: 'error',
      });
      console.log(error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const formElements = Array.from(e.target.form.elements);
      const index = formElements.indexOf(e.target);
      formElements[index + 1]?.focus();
    }
  };
  const handleCancel = (event) => {
    event.preventDefault();

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary mx-1', // Bootstrap primary button
        denyButton: 'btn btn-danger mx-1', // Bootstrap warning button
        cancelButton: 'btn btn-secondary mx-1', // Bootstrap secondary button
      },
      buttonsStyling: false,
    });
    swalWithBootstrapButtons
      .fire({
        title: 'Are you sure?',
        text: 'All your input will be lost!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel it!',
        cancelButtonText: 'No, keep it',
      })
      .then((result) => {
        if (result.isConfirmed) {
          const form = document.querySelector('.needs-validation');
          form.reset();
          setFormData({
            external_code: '',
            name: '',
            npwp: '',
            contact_name: '',
            contact_title: '',
            address: '',
            city: '',
            state_or_province: '',
            postal_code: '',
            country: '',
            email: '',
            phone: '',
            fax: '',
            payment_terms: '',
            notes: '',
            flag: '',
            segment_type: '',
          });
          form.classList.remove('was-validated');
        }
      });
  };

  return (
    <div>
      <LoadingOverlay show={loading} message={message} />
      <Breadcrumb items={breadcrumbItems}></Breadcrumb>
      <form
        className="needs-validation"
        autocomplete="off"
        noValidate
        onSubmit={handleSubmit}
      >
        <div className="row">
          <div className="col-sm-6">
            <div className="card">
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="validationExtCode" className="form-label">
                    External Code
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    id="validationExtCode"
                    name="external_code"
                    value={formData.external_code}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    required
                  />
                  <div className="invalid-feedback">Cannot be empty.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    id="validationName"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    required
                  />
                  <div className="invalid-feedback">Cannot be empty.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label">NPWP</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    id="validationNPWP"
                    name="npwp"
                    value={formData.npwp}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Contact Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Contact Title</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="contact_title"
                    value={formData.contact_title}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Postal Code</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">State or Province</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="state_or_province"
                    value={formData.state_or_province}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-sm-6">
            <div className="card">
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Email Address</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Fax</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="fax"
                    value={formData.fax}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Payment Terms</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="payment_terms"
                    value={formData.payment_terms}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Flag</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="flag"
                    value={formData.flag}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Segment Type</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="segment_type"
                    value={formData.segment_type}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </div>
              </div>
            </div>
            <div className="button-navigatsi">
              <div className="">
                <button type="submit" className="btn btn-primary">
                  <i className="bi bi-send"></i>
                  <span> Submit</span>
                </button>
              </div>
              <div className="">
                <button
                  type="reset"
                  className="btn btn-danger btn-cancel"
                  onClick={handleCancel}
                >
                  <i className="bi bi-x-circle"></i>
                  <span> Cancel</span>
                </button>
              </div>
              <div className="">
                <Link type="button" className="btn btn-warning" to="/external">
                  <i className="bi bi-arrow-left-circle"></i>
                  <span> Back</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ExternalAdd;
